import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    IonToggle,
    IonToast,
    useIonRouter
} from '@ionic/react';
import { saveOutline, closeOutline, checkmarkCircle, closeCircle } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './View.css';
import Loader from '../components/Loader';
import LocationSelector from '../components/LocationSelector';

const AdminUserEdit = () => {
    const router = useIonRouter();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState({
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role_id: '4',
        organization_id: '',
        is_active: true,
        country: '',
        state: '',
        local_government: ''
    });
    const [organizations, setOrganizations] = useState([]);
    const [roles, setRoles] = useState([]);
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });



    useEffect(() => {
        fetchUserData();
        fetchOrganizations();
        fetchRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const userId = localStorage.getItem('userId');

            if (!userId) {
                setToast({ show: true, message: 'Please login to continue', color: 'danger' });
                setTimeout(() => router.push('/page/login'), 2000);
                return;
            }

            const response = await axios.get(
                `${API_BASE_URL}/manage_users.php?user_id=${userId}&filter=all`
            );

            if (response.data.success) {
                const targetUser = response.data.users.find(u => u.id === id);

                if (targetUser) {
                    setUser({
                        id: targetUser.id,
                        first_name: targetUser.first_name || '',
                        last_name: targetUser.last_name || '',
                        email: targetUser.email || '',
                        phone: targetUser.phone || '',
                        role_id: targetUser.role_id || '4',
                        organization_id: targetUser.organization_id || '',
                        is_active: targetUser.is_active === '1' || targetUser.is_active === 1,
                        country: targetUser.country || '',
                        state: targetUser.state || '',
                        local_government: targetUser.local_government || ''
                    });
                } else {
                    setToast({ show: true, message: 'User not found', color: 'danger' });
                    setTimeout(() => router.goBack(), 2000);
                }
            }
        } catch (error) {
            setToast({ show: true, message: 'Failed to load user data', color: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/get_organization_types.php`
            );
            if (response.data && Array.isArray(response.data)) {
                setOrganizations(response.data);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchRoles = async () => {
        // Hardcoded roles based on database
        setRoles([
            { id: '1', name: 'Super Admin' },
            { id: '2', name: 'Organization Admin' },
            { id: '3', name: 'Moderator' },
            { id: '4', name: 'Regular User' },
            { id: '5', name: 'Viewer' }
        ]);
    };

    const handleInputChange = (field, value) => {
        setUser(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        try {
            // Validation
            if (!user.email || !user.first_name || !user.last_name) {
                setToast({ show: true, message: 'Please fill in all required fields', color: 'warning' });
                return;
            }

            setSaving(true);
            const userId = localStorage.getItem('userId');

            const response = await axios.put(
                `${API_BASE_URL}/manage_users.php`,
                {
                    user_id: userId,
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    name: `${user.first_name} ${user.last_name}`,
                    email: user.email,
                    phone: user.phone,
                    role_id: user.role_id,
                    organization_id: user.organization_id,
                    is_active: user.is_active ? 1 : 0,
                    country: user.country,
                    state: user.state,
                    local_government: user.local_government
                }
            );

            if (response.data.success) {
                setToast({ show: true, message: 'User updated successfully!', color: 'success' });
                setTimeout(() => router.push('/admin/users'), 1500);
            } else {
                setToast({ show: true, message: response.data.error || 'Failed to update user', color: 'danger' });
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setToast({ show: true, message: error.response?.data?.error || 'Failed to update user', color: 'danger' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        router.goBack();
    };


    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/admin/users" />
                    </IonButtons>
                    <IonTitle>Edit User</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={handleCancel} color="light">
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                {loading && <Loader message="Extracting User Profile..." />}
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>User Information</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonItem>
                            <IonLabel position="stacked">First Name *</IonLabel>
                            <IonInput
                                value={user.first_name}
                                onIonChange={(e) => handleInputChange('first_name', e.detail.value)}
                                placeholder="Enter first name"
                                required
                            />
                        </IonItem>

                        <IonItem>
                            <IonLabel position="stacked">Last Name *</IonLabel>
                            <IonInput
                                value={user.last_name}
                                onIonChange={(e) => handleInputChange('last_name', e.detail.value)}
                                placeholder="Enter last name"
                                required
                            />
                        </IonItem>

                        <IonItem>
                            <IonLabel position="stacked">Email *</IonLabel>
                            <IonInput
                                type="email"
                                value={user.email}
                                onIonChange={(e) => handleInputChange('email', e.detail.value)}
                                placeholder="Enter email"
                                required
                            />
                        </IonItem>

                        <IonItem>
                            <IonLabel position="stacked">Phone</IonLabel>
                            <IonInput
                                type="tel"
                                value={user.phone}
                                onIonChange={(e) => handleInputChange('phone', e.detail.value)}
                                placeholder="Enter phone number"
                            />
                        </IonItem>

                        <div className="pt-2 px-1">
                            <LocationSelector 
                                country={user.country}
                                state={user.state}
                                localGovernment={user.local_government}
                                onChange={(field, value) => {
                                    const mapping = { localGovernment: 'local_government' };
                                    const finalField = mapping[field] || field;
                                    handleInputChange(finalField, value);
                                }}
                                required={true}
                            />
                        </div>

                        <IonItem>
                            <IonLabel position="stacked">Role *</IonLabel>
                            <IonSelect
                                value={user.role_id}
                                onIonChange={(e) => handleInputChange('role_id', e.detail.value)}
                            >
                                {roles.map(role => (
                                    <IonSelectOption key={role.id} value={role.id}>
                                        {role.name}
                                    </IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>

                        <IonItem>
                            <IonLabel position="stacked">Organization</IonLabel>
                            <IonSelect
                                value={user.organization_id}
                                onIonChange={(e) => handleInputChange('organization_id', e.detail.value)}
                                placeholder="Select organization"
                            >
                                <IonSelectOption value="">None</IonSelectOption>
                                {organizations.map(org => (
                                    <IonSelectOption key={org.id} value={org.id}>
                                        {org.name}
                                    </IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>

                        <IonItem>
                            <IonLabel>Active Status</IonLabel>
                            <IonToggle
                                checked={user.is_active}
                                onIonChange={(e) => handleInputChange('is_active', e.detail.checked)}
                            />
                        </IonItem>

                        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                            <IonButton expand="block" onClick={handleSave} disabled={saving}>
                                <IonIcon slot="start" icon={saveOutline} />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </IonButton>
                            <IonButton expand="block" color="light" onClick={handleCancel}>
                                <IonIcon slot="start" icon={closeOutline} />
                                Cancel
                            </IonButton>
                        </div>
                    </IonCardContent>
                </IonCard>
            </IonContent>

            {/* Toast Notification */}
            <IonToast
                isOpen={toast.show}
                onDidDismiss={() => setToast({ ...toast, show: false })}
                message={toast.message}
                duration={3000}
                color={toast.color}
                position="top"
                icon={toast.color === 'success' ? checkmarkCircle : closeCircle}
                buttons={[
                    {
                        text: 'Dismiss',
                        role: 'cancel'
                    }
                ]}
            />
        </IonPage>
    );
};

export default AdminUserEdit;
